<template>
  <Popup
    :width="1300"
    :modal-card-classes="['edit-path-card']"
    @closePopup="uiContentStore.closeEditContentPathModal"
  >
    <template v-slot:header>
      <h3>Edit path</h3>
    </template>
    <template v-slot:body>
      <div class="toggle-check-container margin-b-standard">
        <label class="toggle-check">
          <input
            type="checkbox"
            aria-label="edit"
            :value="isLongPathNameEnabled"
            data-testing="toggle-edit-mode"
            class="editable"
            v-model="isLongPathNameEnabled"
          />
          <span
            data-on-text="Long path name"
            data-off-text="Short path name"
          ></span>
        </label>
      </div>
      <div class="margin-b-standard">
        <div>
          <RightSidebarTitle :title="'Select path'" />
          <div class="path-item">
            <select v-model="newParentId">
              <option
                :selected="newParentId === structure.rootSection.id"
                :value="structure.rootSection.id"
                >{{ product.name }}
              </option>
              <option
                v-for="item in sortedPathsToDisplay"
                :key="item.lastChildSectionId"
                :value="item.lastChildSectionId"
                :selected="item.lastChildSectionId === newParentId"
                >{{ item.pathString }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div class="margin-b-standard">
        <RightSidebarTitle :title="'Old path'" />
        <ContentLocationPath
          :product="product"
          :path="path"
          :section-name="sectionName"
          :section-id="sectionId"
          :section-icon-id="sectionIconId"
          :section-external-id="sectionExternalId"
          :is-clickable="false"
          :is-long-path-name-enabled="isLongPathNameEnabled"
        />
      </div>
      <div>
        <RightSidebarTitle :title="'New path'" />
        <ContentLocationPath
          :product="product"
          :path="newPath"
          :section-name="sectionName"
          :section-id="sectionId"
          :section-icon-id="sectionIconId"
          :section-external-id="sectionExternalId"
          :is-clickable="false"
          :is-long-path-name-enabled="isLongPathNameEnabled"
        />
      </div>
      <ErrorMessage v-if="serverError" :message="serverError" />
    </template>
    <template v-slot:footer>
      <button
        :disabled="isSubmitting"
        class="btn-2"
        type="button"
        @click="uiContentStore.closeEditContentPathModal"
      >
        Cancel
      </button>
      <button
        class="btn-1"
        type="button"
        @click="updatePath"
        :disabled="isSaveBtnDisabled"
      >
        Save
      </button>
    </template>
  </Popup>
</template>
<script lang="ts" setup>
import { computed, onMounted, PropType, ref } from "vue";

import { SECTION } from "@/constants/assets.constant";
import { showToast } from "@/services/notification-manager";
import { useContentStore } from "@/stores/content.store";
import { useUIContentStore } from "@/stores/ui-content.store";

import { AssetLocation } from "@/models/assets.model";
import { GeneratedContentStructure } from "@/models/content-structure.model";
import { PathToDisplay } from "@/models/product-section.model";
import { ProductItem } from "@/models/product.model";

import csHelper from "@/utils/product/content-structure-helpers";
import {
  getPossiblePaths,
  getSortedPathsForDisplay
} from "@/utils/product/get-path-to-section";

import ErrorMessage from "@/components/ErrorMessage.vue";
import Popup from "@/components/Popup.vue";
import ContentLocationPath from "@/components/right-sidebars/content-sidebar/content-location/ContentLocationPath.vue";
import RightSidebarTitle from "@/components/right-sidebars/ui/RightSidebarTitle.vue";

const uiContentStore = useUIContentStore();
const contentStore = useContentStore();

const props = defineProps({
  product: {
    required: true,
    type: Object as PropType<ProductItem>
  },
  structure: {
    required: true,
    type: Object as PropType<GeneratedContentStructure>
  },
  path: {
    required: true,
    type: Array as PropType<AssetLocation[]>
  },
  sectionName: {
    required: true,
    type: String
  },
  sectionId: {
    required: true,
    type: Number
  },
  sectionIconId: {
    required: true,
    type: Number
  },
  sectionExternalId: {
    required: true,
    type: String
  }
});

const oldParentId = ref<number | null>(null);
const newParentId = ref<number | null>(null);
const currentPath = computed<AssetLocation[]>(() => {
  return props.structure.rootSection
    ? csHelper.getPathToSectionWithIds(
        props.structure.rootSection,
        props.sectionId
      )
    : [];
});
const setParentIds = (): void => {
  if (props.structure.rootSection) {
    if (currentPath.value.length > 0) {
      oldParentId.value = currentPath.value[currentPath.value.length - 1].id;
      newParentId.value = oldParentId.value;
    } else {
      oldParentId.value = props.structure.rootSection.id;
      newParentId.value = oldParentId.value;
    }
  }
};
onMounted(() => {
  setParentIds();
});

const isSubmitting = ref<boolean>(false);
const isLongPathNameEnabled = ref<boolean>(true);
const isSaveBtnDisabled = computed<boolean>(() => {
  return (
    newParentId.value === oldParentId.value ||
    newParentId.value === 0 ||
    newParentId.value === props.sectionId ||
    isSubmitting.value
  );
});

const possiblePaths = computed<AssetLocation[][]>(() => {
  const paths = getPossiblePaths(
    props.product.sections,
    props.structure.rootSection
  );
  return paths.filter((path: AssetLocation[]): boolean => {
    // remove all paths to child sections of the current section
    return !path.some((pathItem: AssetLocation): boolean => {
      return pathItem.id === props.sectionId;
    });
  });
});
const sortedPathsToDisplay = computed<PathToDisplay[]>(() => {
  return getSortedPathsForDisplay(possiblePaths.value, props.product.name);
});

const newPath = computed<AssetLocation[] | undefined>(() => {
  if (newParentId.value !== oldParentId.value) {
    if (
      props.structure.rootSection &&
      newParentId.value === props.structure.rootSection.id
    ) {
      return [];
    }
    return possiblePaths.value.find(
      (possiblePath: AssetLocation[]): boolean => {
        return (
          !!newParentId.value &&
          possiblePath[possiblePath.length - 1].id === newParentId.value
        );
      }
    );
  }
  return props.path;
});

const serverError = ref<string>("");
const updatePath = () => {
  if (newParentId.value) {
    isSubmitting.value = true;
    const payload = {
      productIds: [props.product.id],
      sections: [
        {
          id: props.sectionId,
          new_parent_section_id: newParentId.value
        }
      ]
    };
    contentStore
      .upsertSections(payload)
      .then(response => {
        if (response.status === 200) {
          showToast({
            message: SECTION.SUCCESS.internalPath,
            type: "is-success"
          });
          uiContentStore.closeEditContentPathModal();
        }
      })
      .catch(error => {
        serverError.value = error.message;
      })
      .finally(() => {
        isSubmitting.value = false;
      });
  }
};
</script>
<style lang="scss">
.edit-path-card.modal-card {
  @media screen and (min-width: 769px) {
    width: 1300px;
  }
}
</style>
<style scoped lang="scss">
.toggle-check-container {
  display: flex;
  justify-content: flex-end;
  border-bottom: 1px solid $border-color;
  padding-bottom: 0.5rem;

  .toggle-check {
    transform: scale(0.75);
  }
}

.path-item {
  display: flex;
  align-items: center;
}
</style>
